import React from 'react';
import { STORIES } from '@/data/pipeline';
import { PROGRAMS } from '@/data/seed';
import { fmtDate } from '@/lib/fmt';
import { STORY_CARD } from '@/constants/testIds';
import { BookOpen, Newspaper, MapPin } from 'lucide-react';
import KpiCard from '@/components/KpiCard';
import { ProgramChip } from '@/components/UiBits';
import { useDashboard } from '@/context/DashboardContext';

export default function Stories() {
  const { PROGRAMS: P } = useDashboard();
  const featured = STORIES.items.find((s) => s.featured);
  const rest = STORIES.items.filter((s) => !s.featured);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="overline">Stories of Change</div>
          <h1>Field notes, one household at a time</h1>
          <div className="sub">Editorial reporting from grant-funded programmes — the stories we share with donors, media, and our own board.</div>
        </div>
      </div>

      <div className="kpi-row">
        <KpiCard icon={BookOpen}  iconCls="ic-gold"  value={STORIES.published}       label="Stories Published (12 mo)" delta={['up','12 new this quarter']} />
        <KpiCard icon={MapPin}    iconCls="ic-blue"  value={STORIES.districts}       label="Districts Covered" />
        <KpiCard icon={BookOpen}  iconCls="ic-green" value={STORIES.inDonorReports}  label="Used in Donor Reports" delta={['up','8 added']} />
        <KpiCard icon={Newspaper} iconCls="ic-amber" value={STORIES.mediaPickups}    label="Media Pickups" />
      </div>

      {featured && (
        <div className="ed-feature" data-testid={STORY_CARD('featured')}>
          <div className="ed-img" style={{ minHeight: 320 }}>
            <img src={featured.img} alt={featured.title} loading="lazy" />
            <div className="ed-sector">{PROGRAMS[featured.program].name}</div>
          </div>
          <div className="ed-body">
            <div className="overline">Featured · {fmtDate(featured.date)}</div>
            <div className="ed-title">{featured.title}</div>
            <div className="ed-excerpt">{featured.excerpt}</div>
            <div className="ed-quote">
              “{featured.quote}”
              <span className="who">— {featured.who}</span>
            </div>
            <div className="ed-meta">
              <span className="k">Geography</span><span className="v">{featured.district}, {featured.state}</span>
              <span className="k">Donor</span><span className="v">{featured.donor}</span>
              <span className="k">Programme</span><span className="v">{featured.grantName}</span>
            </div>
            <div className="text-[11.5px] text-[color:var(--gray-400)] mt-2">
              Field note · <b className="text-[color:var(--gray-600)]">{featured.author}</b> · {fmtDate(featured.date)}
            </div>
          </div>
        </div>
      )}

      <div className="ed-grid">
        {rest.map((s, i) => (
          <article key={i} data-testid={STORY_CARD(i)} className="ed-card">
            <div className="ed-img">
              <img src={s.img} alt={s.title} loading="lazy" />
              <div className="ed-sector">{PROGRAMS[s.program].name}</div>
            </div>
            <div className="ed-body">
              <div className="overline">{fmtDate(s.date)} · {s.district}, {s.state}</div>
              <div className="ed-title">{s.title}</div>
              <div className="ed-excerpt">{s.excerpt}</div>
              <div className="ed-quote">
                “{s.quote}”
                <span className="who">— {s.who}</span>
              </div>
              <div className="ed-meta">
                <span className="k">Donor</span><span className="v">{s.donor}</span>
                <span className="k">Grant</span><span className="v">{s.grantName}</span>
              </div>
            </div>
            <div className="ed-foot">
              <div className="fn">Field note · <b>{s.author}</b></div>
              <ProgramChip program={s.program} PROGRAMS={P} />
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
